import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';

// ── E-mail modal (mailto) ────────────────────────────────────
function EmailModal({ contact, onClose, t }) {
  const [subject, setSubject] = useState('');
  const [body, setBody]       = useState('');

  function handleOpen() {
    const mailto = `mailto:${encodeURIComponent(contact.email)}`
      + `?subject=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title">{t('contact_email_btn')}</h5>
                <div className="text-muted small">{t('email_to')}: {contact.name} &lt;{contact.email}&gt;</div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('email_subject')}</label>
                <input className="form-control" value={subject}
                  onChange={e => setSubject(e.target.value)} autoFocus />
              </div>
              <div className="mb-1">
                <label className="form-label small fw-medium">{t('email_body')}</label>
                <textarea className="form-control" rows={10} value={body}
                  onChange={e => setBody(e.target.value)} />
              </div>
              <div className="text-muted small mt-2">
                <i className="bi bi-info-circle me-1" />
                Opent Outlook met de tekst ingevuld — jij verstuurt zelf.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('close')}</button>
              <button type="button" className="btn btn-primary" onClick={handleOpen}>
                <i className="bi bi-envelope-open me-2" />Openen in Outlook
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

// ── Contact modal (nieuw / bewerken) ─────────────────────────
function ContactModal({ contact, onClose, onSave, t }) {
  const isNew = !contact?.id;
  const [form, setForm] = useState({
    name:            contact?.name            || '',
    role:            contact?.role            || '',
    email:           contact?.email           || '',
    phone:           contact?.phone           || '',
    primary_contact: contact?.primary_contact || 0,
    notes:           contact?.notes           || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (isNew) {
        await api.post(`/api/clients/${contact.clientId}/contacts`, form);
      } else {
        await api.put(`/api/contacts/${contact.id}`, form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isNew ? t('contact_new') : t('edit')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <form id="contact-form" onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('contact_name')} *</label>
                    <input className="form-control" value={form.name}
                      onChange={e => set('name', e.target.value)} required autoFocus />
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('contact_role')}</label>
                    <input className="form-control" value={form.role}
                      onChange={e => set('role', e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('contact_email')}</label>
                    <input type="email" className="form-control" value={form.email}
                      onChange={e => set('email', e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('contact_phone')}</label>
                    <input className="form-control" value={form.phone}
                      onChange={e => set('phone', e.target.value)} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('contact_notes')}</label>
                  <textarea className="form-control" rows={2} value={form.notes}
                    onChange={e => set('notes', e.target.value)} />
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="primary_contact"
                    checked={!!form.primary_contact}
                    onChange={e => set('primary_contact', e.target.checked ? 1 : 0)} />
                  <label className="form-check-label small" htmlFor="primary_contact">
                    {t('contact_primary')}
                  </label>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button type="submit" form="contact-form" className="btn btn-primary" disabled={loading}>
                {loading && <span className="spinner-border spinner-border-sm me-2" />}
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

// ── Hoofdpagina ──────────────────────────────────────────────
export default function ClientDetailPage() {
  const { id } = useParams();
  const { t }  = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [client,   setClient]   = useState(null);
  const [contacts, setContacts] = useState([]);
  const [audits,   setAudits]   = useState([]);
  const [loading,  setLoading]  = useState(true);

  const [contactModal, setContactModal] = useState(null); // null | 'new' | contact-obj
  const [emailModal,   setEmailModal]   = useState(null); // null | contact-obj
  const [logoUploading, setLogoUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/api/clients/${id}`),
      api.get(`/api/clients/${id}/contacts`),
      api.get('/api/audits'),
    ]).then(([c, ct, a]) => {
      setClient(c.data);
      setEditForm({
        name: c.data.name, vat_number: c.data.vat_number || '',
        sector: c.data.sector || '', address: c.data.address || '',
        notes: c.data.notes || '', description: c.data.description || '',
      });
      setContacts(ct.data);
      setAudits(a.data.filter(a => String(a.client_id) === String(id) || a.client_name === c.data.name));
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    const fd = new FormData();
    fd.append('logo', file);
    try {
      await api.post(`/api/clients/${id}/logo`, fd);
      load();
    } finally {
      setLogoUploading(false);
      e.target.value = '';
    }
  }

  async function handleLogoDelete() {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/clients/${id}/logo`);
    load();
  }

  async function handleSaveInfo(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/api/clients/${id}`, editForm);
      setEditMode(false);
      load();
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteContact(contactId) {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/contacts/${contactId}`);
    load();
  }

  if (loading) return <div className="page-body"><div className="spinner-border spinner-border-sm" /></div>;
  if (!client) return <div className="page-body text-danger">{t('error')}</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="text-muted-sm mb-1">
            <Link to="/clients" className="text-muted text-decoration-none">{t('nav_clients')}</Link>
            {' / '}
          </div>
          <h2 className="mb-0">{client.name}</h2>
        </div>
        <div className="d-flex gap-2">
          {!editMode
            ? <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditMode(true)}>
                <i className="bi bi-pencil me-1" />{t('edit')}
              </button>
            : <>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(false)}>{t('cancel')}</button>
                <button className="btn btn-primary btn-sm" onClick={handleSaveInfo} disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-2" />}{t('save')}
                </button>
              </>}
        </div>
      </div>

      <div className="page-body">
        <div className="row g-4">

          {/* Links: info + logo */}
          <div className="col-md-4">

            {/* Logo */}
            <div className="card mb-3">
              <div className="card-header fw-semibold small d-flex justify-content-between align-items-center">
                {t('client_logo')}
                <label className="btn btn-sm btn-outline-secondary mb-0" style={{ cursor: 'pointer' }}>
                  {logoUploading
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><i className="bi bi-upload me-1" />Upload</>}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="d-none" disabled={logoUploading} />
                </label>
              </div>
              <div className="card-body text-center py-3">
                {client.logo_filename ? (
                  <>
                    <img
                      src={`/uploads/logos/${client.logo_filename}`}
                      alt={client.name}
                      style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'contain' }}
                    />
                    <div className="mt-2">
                      <button className="btn btn-sm btn-outline-danger" onClick={handleLogoDelete}>
                        <i className="bi bi-trash me-1" />{t('delete')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-muted small py-2">
                    <i className="bi bi-image" style={{ fontSize: 32, display: 'block', marginBottom: 6 }} />
                    {t('client_no_logo')}
                  </div>
                )}
              </div>
            </div>

            {/* Klantinfo */}
            <div className="card">
              <div className="card-header fw-semibold small">{t('client_name')}</div>
              <div className="card-body">
                {editMode ? (
                  <form onSubmit={handleSaveInfo}>
                    {[
                      ['name',       t('client_name'),    'text',  true],
                      ['vat_number', t('client_vat'),     'text',  false],
                      ['sector',     t('client_sector'),  'text',  false],
                      ['address',    t('client_address'), 'text',  false],
                    ].map(([field, label, type, required]) => (
                      <div className="mb-2" key={field}>
                        <label className="form-label small fw-medium">{label}</label>
                        <input type={type} className="form-control form-control-sm"
                          value={editForm[field] || ''}
                          onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                          required={required} />
                      </div>
                    ))}
                    <div className="mb-1">
                      <label className="form-label small fw-medium">Notities</label>
                      <textarea className="form-control form-control-sm" rows={3}
                        value={editForm.notes || ''}
                        onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                  </form>
                ) : (
                  <dl className="mb-0" style={{ fontSize: 13 }}>
                    {[
                      [t('client_vat'),     client.vat_number],
                      [t('client_sector'),  client.sector],
                      [t('client_address'), client.address],
                    ].map(([label, val]) => val ? (
                      <div key={label} className="mb-1">
                        <dt className="text-muted d-inline">{label}: </dt>
                        <dd className="d-inline">{val}</dd>
                      </div>
                    ) : null)}
                    {client.notes && <div className="mt-2 text-muted small">{client.notes}</div>}
                  </dl>
                )}
              </div>
            </div>
          </div>

          {/* Bedrijfsomschrijving — volle breedte */}
          <div className="col-12">
            <div className="card">
              <div className="card-header fw-semibold small d-flex justify-content-between align-items-center">
                Bedrijfsomschrijving
                <span className="text-muted fw-normal" style={{ fontSize: 11 }}>
                  Wordt gebruikt als context bij het genereren van auditvragen
                </span>
              </div>
              <div className="card-body">
                {editMode ? (
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Beschrijf het bedrijf: activiteiten, sector, grootte, IT-omgeving, bijzonderheden…"
                    value={editForm.description || ''}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                  />
                ) : (
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    {client.description
                      ? client.description
                      : <span className="text-muted small">
                          Nog geen omschrijving.{' '}
                          <button className="btn btn-link btn-sm p-0" onClick={() => setEditMode(true)}>Toevoegen</button>
                        </span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rechts: contacten + audits */}
          <div className="col-md-8">

            {/* Contacten */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold small">{t('client_contact')}</span>
                <button className="btn btn-sm btn-outline-primary"
                  onClick={() => setContactModal({ clientId: id })}>
                  <i className="bi bi-plus me-1" />{t('contact_new')}
                </button>
              </div>
              {contacts.length === 0 ? (
                <div className="card-body text-muted small py-3">{t('contact_new')}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('contact_name')}</th>
                        <th>{t('contact_role')}</th>
                        <th>{t('contact_email')}</th>
                        <th>{t('contact_phone')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map(c => (
                        <tr key={c.id}>
                          <td>
                            <span className="fw-medium">{c.name}</span>
                            {c.primary_contact ? (
                              <span className="badge bg-primary ms-2" style={{ fontSize: 10 }}>primair</span>
                            ) : null}
                          </td>
                          <td className="text-muted small">{c.role || '—'}</td>
                          <td>
                            {c.email
                              ? <a href={`mailto:${c.email}`} className="text-decoration-none small">{c.email}</a>
                              : <span className="text-muted small">—</span>}
                          </td>
                          <td className="text-muted small">{c.phone || '—'}</td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end">
                              {c.email && (
                                <button className="btn btn-sm btn-outline-primary"
                                  onClick={() => setEmailModal(c)}
                                  title={t('contact_email_btn')}>
                                  <i className="bi bi-envelope" />
                                </button>
                              )}
                              <button className="btn btn-sm btn-outline-secondary"
                                onClick={() => setContactModal(c)}>
                                <i className="bi bi-pencil" />
                              </button>
                              <button className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteContact(c.id)}>
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Audits */}
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="fw-semibold small">{t('client_audits')}</span>
                <span className="text-muted small">{audits.length}</span>
              </div>
              {audits.length === 0 ? (
                <div className="card-body text-muted small py-3">—</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-clickable mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('audit_name')}</th>
                        <th>{t('audit_framework')}</th>
                        <th>{t('audit_date')}</th>
                        <th>{t('audit_status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audits.map(a => (
                        <tr key={a.id} onClick={() => navigate(`/audits/${a.id}`)}>
                          <td className="fw-medium">{a.name}</td>
                          <td className="text-muted small">{a.framework_name}</td>
                          <td className="text-muted small">
                            {a.audit_date ? new Date(a.audit_date).toLocaleDateString() : '—'}
                          </td>
                          <td><StatusBadge status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Modals */}
      {contactModal && (
        <ContactModal
          contact={contactModal}
          t={t}
          onClose={() => setContactModal(null)}
          onSave={() => { setContactModal(null); load(); }}
        />
      )}
      {emailModal && (
        <EmailModal
          contact={emailModal}
          t={t}
          onClose={() => setEmailModal(null)}
        />
      )}
    </>
  );
}
